/**
 * Autocomplete for the filter language: given the text and where the caret
 * is, what could the word under the caret become.
 *
 * Pure, like query.ts. Each suggestion says what to put in place of the word
 * under the caret, and whether that finishes the term (a value, which then
 * becomes a chip) or leaves the caret inside it (a key, waiting for its
 * value).
 */
import type { Vocab } from '../../vocab';
import {
  CUSTOM_PREFIX, DATE_PRESETS, FIELD_NAME, KEY_NAMES, PERSON_FIELDS, STATUSES, isPersonKey, keyIdFor,
  parseDate, quote, type Chip, type KeyId, type Named,
} from './query';

export type SuggestData = {
  campaigns: Named[] | null;
  suppliers: Named[] | null;
  buyers: Named[] | null;
  sources: string[] | null;
  customKeys: string[] | null;
  /** Whether `repeats:` is offered: false for anyone who may not see people. */
  people?: boolean;
  /** Whether `owner:` is offered: false when owners are not in use here. */
  ownership?: boolean;
  /** Members who can own a record, for `owner:` names. */
  owners?: Named[] | null;
  /** Whether `score:` and `band:` are offered: false where scores are not shown. */
  scoring?: boolean;
  /** The score bands, by key and name, for `band:`. */
  bands?: Named[] | null;
  /**
   * The custom fields of the campaigns the query is narrowed to, once
   * fetched. Null (or left out) when there is no such campaign or its
   * fields have not arrived, and the suggestions are then account-wide.
   */
  campaignKeys?: CampaignKeys | null;
  /**
   * The organisation's lead properties, once fetched: `custom.` offers their
   * keys first, with labels, and after the colon a property's options,
   * true or false, or a comparison, by its type. Null (or left out) until
   * they arrive, and everything then works as before.
   */
  properties?: SuggestProperty[] | null;
  today: string;
};

/** A lead property as the search bar needs it. */
export type SuggestProperty = {
  key: string;
  label: string;
  /** The property's type: `select`, `boolean`, `number`, `date` and so on. */
  type: string;
  /** A select's stored values, in order. */
  options?: string[];
};

/** One or more campaigns' custom fields, for ranking them first. */
export type CampaignKeys = {
  /** The campaigns, so the group can be named after them. */
  campaigns: Named[];
  /** Fields the campaigns define, in the campaign's own order. */
  defined: string[];
  /** Fields seen on their recent leads, most common first. */
  seen: string[];
};

/**
 * The campaigns a query is narrowed to: every `campaign:` chip, and every
 * `from:` chip that resolved to a campaign, that is neither negated nor in
 * error. In order, each once. Its custom fields are then offered first.
 */
export function chosenCampaigns(chips: Chip[]): Named[] {
  const out: Named[] = [];
  for (const c of chips) {
    if (c.neg || c.error || c.filter?.kind !== 'campaign') continue;
    const { id, name } = c.filter;
    if (!out.some((n) => n.id === id)) out.push({ id, name });
  }
  return out;
}

export type Suggestion = {
  id: string;
  label: string;
  detail?: string;
  /** Replaces the word under the caret. */
  insert: string;
  /** True when this finishes the term, so a space follows and it becomes a chip. */
  done: boolean;
  /** A record's name, shown in the body font rather than as filter syntax. */
  isName?: boolean;
};

export type SuggestionGroup = { key: string; label: string; items: Suggestion[] };

export type Suggestions = {
  /** The span of text the suggestion replaces. */
  start: number;
  end: number;
  groups: SuggestionGroup[];
  /** What kind of data the word under the caret wants, so it can be fetched. */
  wants: 'custom_keys' | 'sources' | null;
};

const NONE = (start: number, end: number): Suggestions => ({ start, end, groups: [], wants: null });

/** The word the caret is in or just after, quotes and all. */
export function wordAt(text: string, caret: number): { start: number; end: number; word: string } {
  // Walk from the start so quotes are paired the way the tokenizer pairs them.
  let start = 0;
  let inQuote = false;
  for (let i = 0; i < caret; i++) {
    const c = text[i] ?? '';
    if (c === '"') inQuote = !inQuote;
    else if (!inQuote && /\s/.test(c)) start = i + 1;
  }
  let end = caret;
  while (end < text.length && (inQuote || !/\s/.test(text[end] ?? ''))) {
    if (text[end] === '"') inQuote = !inQuote;
    end++;
  }
  return { start, end, word: text.slice(start, caret) };
}

/**
 * The name each key is offered under, in the app's words: `stream:` and
 * `tag:`, though `campaign:` and `source:` still work and stay canonical.
 */
export function keyName(id: Exclude<KeyId, 'custom'>): string {
  if (id === 'campaign') return 'stream';
  if (id === 'source') return 'tag';
  return KEY_NAMES[id][0] ?? id;
}

/** One line on what each key does, in this organisation's words. */
export function keyDescription(id: KeyId, v: Vocab): string {
  switch (id) {
    case 'campaign': return `${v.One('campaign')}, by name`;
    case 'supplier': return `${v.One('supplier')} that sent it, by name`;
    case 'buyer': return `${v.One('buyer')} it went to, by name`;
    case 'from': return `${v.One('campaign')} or ${v.one('supplier')} name, or a start date`;
    case 'status': return `Delivery status, or ${v.p.sold}`;
    case 'date': return 'When it arrived: today, last7, 2026-10-01 or a range';
    case 'after': return 'Arrived on or after a date';
    case 'before': return 'Arrived on or before a date';
    case 'value': return `${v.p.leadValue}: >5, <=20, 5..20`;
    case 'source': return `${v.p.sourceTag} on the record`;
    case 'has': return 'Has a custom field filled in';
    case 'repeats': return `Its ${v.one('person')} has sent more than one: >1, >=3`;
    case 'owner': return `Whose ${v.one('person')} it is: me, none, or a name`;
    case 'score': return 'Its score: >50, <=20, 20..50';
    case 'band': return 'Its score band, by name: band:hot';
    case 'custom': return 'A custom field: custom.boiler_age:>10';
    case 'first': return 'First name contains it. =sam is exactly, sam* starts with';
    case 'last': return 'Last name contains it. =smith is exactly, sm* starts with';
    case 'name': return 'Full name contains it';
    case 'email': return 'Email address contains it';
    case 'phone': return 'Phone number contains it';
    case 'company': return 'Company name contains it';
  }
}

/** The order keys are offered in when nothing narrows them. */
const KEY_ORDER: Exclude<KeyId, 'custom'>[] = [
  'campaign', 'supplier', 'buyer', 'from', 'status', 'date', 'after', 'before', 'value', 'source', 'has',
  'repeats', 'owner', 'score', 'band', 'first', 'last', 'name', 'email', 'phone', 'company',
];

/**
 * The keys this person may use: `repeats:` and `owner:` only for someone who
 * sees people, `owner:` only while owners are in use, and `score:` and
 * `band:` only where scores are shown.
 */
function keysFor(data: SuggestData): Exclude<KeyId, 'custom'>[] {
  return KEY_ORDER.filter((k) => {
    if (k === 'repeats') return data.people !== false;
    if (k === 'owner') return data.people !== false && data.ownership !== false;
    if (k === 'score' || k === 'band') return data.scoring !== false;
    return true;
  });
}

/**
 * How well a name matches what was typed: 0 is no match, higher is better.
 * Prefix beats word start beats substring beats letters in order.
 */
export function rank(name: string, typed: string): number {
  const n = name.toLowerCase();
  const q = typed.toLowerCase().trim();
  if (!q) return 1;
  if (n === q) return 100;
  if (n.startsWith(q)) return 80;
  if (n.split(/[\s\-_/]+/).some((w) => w.startsWith(q))) return 60;
  if (n.includes(q)) return 40;
  let i = 0;
  for (const c of n) if (c === q[i]) i++;
  return i === q.length ? 10 : 0;
}

function best<T>(items: T[], name: (t: T) => string, typed: string, limit: number): T[] {
  return items
    .map((t) => ({ t, r: rank(name(t), typed) }))
    .filter((x) => x.r > 0)
    .sort((a, b) => b.r - a.r || name(a.t).localeCompare(name(b.t)))
    .slice(0, limit)
    .map((x) => x.t);
}

const PER_GROUP = 6;

export function statusLabel(status: string, v: Vocab): string {
  if (status === 'sold') return v.p.Sold;
  if (status === 'unsold') return v.p.Unsold;
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** OR, offered once its first letter is typed. */
const OR_SUGGESTION: Suggestion = {
  id: 'op:or', label: 'OR', detail: 'Either side may match. Group with ( and )', insert: 'OR', done: true,
};

export function suggest(text: string, caret: number, data: SuggestData, v: Vocab): Suggestions {
  const found = wordAt(text, caret);
  // An opening bracket stays where it is; the word after it is what is suggested for.
  const lead = /^\(+/.exec(found.word)?.[0] ?? '';
  const start = found.start + lead.length;
  const { end } = found;
  const word = found.word.slice(lead.length);
  if (!word) {
    if (!lead) return NONE(start, end);
    // Just opened a bracket: what can go in it.
    const items = keysFor(data).map((id) => {
      const k = keyName(id);
      return { id: `key:${id}`, label: `${k}:`, detail: keyDescription(id, v), insert: `${k}:`, done: false };
    });
    return { start, end, wants: null, groups: [{ key: 'keys', label: 'Filters', items }] };
  }
  const neg = word.startsWith('-') ? '-' : '';
  const body = neg ? word.slice(1) : word;

  // A custom field name, still being typed.
  const custom = /^custom\.([A-Za-z0-9_]*)$/i.exec(body);
  if (custom) {
    return {
      start, end, wants: 'custom_keys',
      groups: customKeyGroups(data, custom[1] ?? '', v, (k) => ({
        id: `custom:${k}`, label: k, insert: `${neg}${CUSTOM_PREFIX}${k}:`, done: false,
      })),
    };
  }

  const colon = body.indexOf(':');
  if (colon === -1) {
    // A key, still being typed. Only once it looks like the start of one.
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(body)) return NONE(start, end);
    const typed = body.toLowerCase();
    const items: Suggestion[] = [];
    for (const id of keysFor(data)) {
      const shown = keyName(id);
      const hit = [shown, ...KEY_NAMES[id]].find((n) => n.startsWith(typed));
      if (!hit) continue;
      items.push({ id: `key:${id}`, label: `${hit}:`, detail: keyDescription(id, v), insert: `${neg}${hit}:`, done: false });
    }
    if ('custom'.startsWith(typed)) {
      items.push({ id: 'key:custom', label: 'custom.', detail: keyDescription('custom', v), insert: `${neg}custom.`, done: false });
    }
    const values = valueMatches(typed, neg, data, v);
    const groups: SuggestionGroup[] = [];
    if (values.length) groups.push({ key: 'values', label: 'Filter by', items: values });
    if (items.length) groups.push({ key: 'keys', label: 'Filters', items });
    if (!neg && 'or'.startsWith(typed)) groups.push({ key: 'ops', label: 'Combine', items: [OR_SUGGESTION] });
    return { start, end, wants: typed.length >= VALUE_MIN ? 'sources' : null, groups };
  }

  const keyText = body.slice(0, colon);
  const raw = body.slice(colon + 1);
  const typed = raw.replace(/^"/, '').replace(/"$/, '');
  const id = keyIdFor(keyText);
  const term = (k: string, value: string) => `${neg}${k}:${quote(value)}`;
  const names = (key: string, list: Named[] | null, k: string, label: string): SuggestionGroup | null => {
    if (!list) return null;
    const hits = best(list, (n) => n.name, typed, PER_GROUP);
    if (!hits.length) return null;
    return {
      key,
      label,
      items: hits.map((n) => ({ id: `${key}:${n.id}`, label: n.name, insert: term(k, n.name), done: true, isName: true })),
    };
  };
  const keep = (groups: (SuggestionGroup | null)[]) => groups.filter((g): g is SuggestionGroup => !!g && g.items.length > 0);
  const dates = (k: string): SuggestionGroup => {
    const words = DATE_PRESETS.map((p) => p.word).filter((w) => !typed || w.startsWith(typed.toLowerCase()));
    const items: Suggestion[] = words.map((w) => ({ id: `date:${w}`, label: w, detail: dateDetail(w), insert: term(k, w), done: true }));
    // Something typed that is already a date: offer it as it stands.
    if (typed && !words.includes(typed.toLowerCase()) && parseDate(typed, data.today)) {
      items.unshift({ id: `date:${typed}`, label: typed, insert: term(k, typed), done: true });
    }
    return { key: 'dates', label: 'Dates', items };
  };

  switch (id) {
    case 'campaign':
      return { start, end, wants: null, groups: keep([names('campaigns', data.campaigns, keyText, v.Many('campaign'))]) };
    case 'supplier':
      return { start, end, wants: null, groups: keep([names('suppliers', data.suppliers, keyText, v.Many('supplier'))]) };
    case 'buyer':
      return { start, end, wants: null, groups: keep([names('buyers', data.buyers, keyText, v.Many('buyer'))]) };
    case 'from': {
      // Both kinds, grouped. Choosing a name rewrites the key to say which it is.
      const datesGroup = dates(keyText);
      return {
        start, end, wants: null,
        groups: keep([
          names('campaigns', data.campaigns, keyName('campaign'), v.Many('campaign')),
          names('suppliers', data.suppliers, keyName('supplier'), v.Many('supplier')),
          typed ? datesGroup : { ...datesGroup, items: datesGroup.items.slice(0, 4) },
        ]),
      };
    }
    case 'status': {
      const all = Object.keys(STATUSES);
      const hits = typed ? best(all, (s) => s, typed, all.length) : all;
      return {
        start, end, wants: null,
        groups: keep([{
          key: 'statuses',
          label: 'Statuses',
          items: hits.map((s) => ({
            id: `status:${s}`, label: s, detail: statusLabel(s, v) === s ? undefined : statusLabel(s, v),
            insert: term(keyText, s), done: true,
          })),
        }]),
      };
    }
    case 'date':
    case 'after':
    case 'before':
      return { start, end, wants: null, groups: keep([dates(keyText)]) };
    case 'value': {
      const examples = [
        { v: '>', d: 'More than' }, { v: '>=', d: 'At least' }, { v: '<', d: 'Less than' },
        { v: '<=', d: 'At most' },
      ];
      return {
        start, end, wants: null,
        groups: typed ? [] : [{
          key: 'value',
          label: 'Compare',
          items: examples.map((e) => ({ id: `value:${e.v}`, label: `${e.v}`, detail: e.d, insert: `${neg}${keyText}:${e.v}`, done: false })),
        }],
      };
    }
    case 'score': {
      if (data.scoring === false || typed) return NONE(start, end);
      const items: Suggestion[] = [
        { id: 'score:>50', label: '>50', detail: 'More than 50', insert: `${keyText}:>50`, done: true },
        { id: 'score:20..50', label: '20..50', detail: '20 to 50', insert: `${keyText}:20..50`, done: true },
        { id: 'score:<20', label: '<20', detail: 'Less than 20', insert: `${keyText}:<20`, done: true },
      ];
      return { start, end, wants: null, groups: [{ key: 'score', label: 'Compare', items }] };
    }
    case 'band': {
      if (data.scoring === false) return NONE(start, end);
      const hits = best(data.bands ?? [], (b) => b.name, typed, 8);
      return {
        start, end, wants: null,
        groups: keep([{
          key: 'bands',
          label: 'Bands',
          items: hits.map((b) => ({ id: `band:${b.id}`, label: b.name, insert: term(keyText, b.name.toLowerCase()), done: true })),
        }]),
      };
    }
    case 'repeats': {
      if (data.people === false || typed) return NONE(start, end);
      const items: Suggestion[] = [
        { id: 'repeats:>1', label: '>1', detail: 'More than one', insert: `${keyText}:>1`, done: true },
        { id: 'repeats:>=3', label: '>=3', detail: 'Three or more', insert: `${keyText}:>=3`, done: true },
      ];
      return { start, end, wants: null, groups: [{ key: 'repeats', label: 'Compare', items }] };
    }
    case 'owner': {
      if (data.people === false || data.ownership === false) return NONE(start, end);
      const fixed: Suggestion[] = [
        { id: 'owner:me', label: 'me', detail: 'Mine', insert: term(keyText, 'me'), done: true },
        { id: 'owner:none', label: 'none', detail: 'Nobody owns them', insert: term(keyText, 'none'), done: true },
      ].filter((s) => !typed || s.label.startsWith(typed.toLowerCase()));
      return {
        start, end, wants: null,
        groups: keep([
          { key: 'owner-words', label: v.One('owner'), items: fixed },
          names('owners', data.owners ?? null, keyText, 'Members'),
        ]),
      };
    }
    case 'source': {
      const hits = best(data.sources ?? [], (s) => s, typed, 8);
      return {
        start, end, wants: 'sources',
        groups: keep([{
          key: 'sources',
          label: v.p.sourceTagMany,
          items: hits.map((s) => ({ id: `source:${s}`, label: s, insert: term(keyText, s), done: true })),
        }]),
      };
    }
    case 'has':
      return {
        start, end, wants: 'custom_keys',
        groups: customKeyGroups(data, typed, v, (k) => ({ id: `has:${k}`, label: k, insert: term(keyText, k), done: true })),
      };
    case 'custom': {
      const field = keyText.slice(CUSTOM_PREFIX.length);
      if (!FIELD_NAME.test(field)) return NONE(start, end);
      const prop = (data.properties ?? []).find((p) => p.key.toLowerCase() === field.toLowerCase());
      if (prop) {
        // Asks for the properties, so they arrive while the value is typed.
        return { start, end, wants: 'custom_keys', groups: propertyValues(prop, keyText, typed, neg, term, data.today) };
      }
      if (typed) return { ...NONE(start, end), wants: data.properties ? null : 'custom_keys' };
      const items: Suggestion[] = [
        { id: 'cv:true', label: 'true', detail: 'Yes', insert: term(keyText, 'true'), done: true },
        { id: 'cv:false', label: 'false', detail: 'No', insert: term(keyText, 'false'), done: true },
        { id: 'cv:>', label: '>', detail: 'More than a number', insert: `${neg}${keyText}:>`, done: false },
        { id: 'cv:<', label: '<', detail: 'Less than a number', insert: `${neg}${keyText}:<`, done: false },
        { id: 'cv:=', label: '=', detail: 'Exactly this text', insert: `${neg}${keyText}:=`, done: false },
      ];
      return { start, end, wants: data.properties ? null : 'custom_keys', groups: [{ key: 'custom-values', label: 'Values', items }] };
    }
    default: {
      // A person field: how the value can match, until one is typed.
      if (!isPersonKey(id) || typed) return NONE(start, end);
      const field = PERSON_FIELDS[id].label.toLowerCase();
      const items: Suggestion[] = [
        { id: 'pv:contains', label: 'text', detail: `Contains it, anywhere in the ${field}`, insert: `${neg}${keyText}:`, done: false },
        { id: 'pv:=', label: '=', detail: 'Is exactly, ignoring case', insert: `${neg}${keyText}:=`, done: false },
      ];
      return { start, end, wants: null, groups: [{ key: 'person-values', label: 'Match', items }] };
    }
  }
}

/** The most custom fields offered, across their groups. */
const CUSTOM_LIMIT = 10;

/**
 * Custom field names matching what was typed.
 *
 * With a campaign in the query, its fields come first under its name: the
 * ones it defines, then the ones seen on its recent leads, then the rest of
 * the account's under "Other fields". Without one, the account's fields as
 * one group.
 */
function customKeyGroups(
  data: SuggestData, typed: string, v: Vocab, item: (k: string) => Suggestion,
): SuggestionGroup[] {
  // The organisation's defined properties first, by key or label, each
  // with its label; then the other fields, without them.
  const props = data.properties ?? [];
  const defined = new Set(props.map((p) => p.key));
  const propHits = props
    .map((p) => ({ p, r: Math.max(rank(p.key, typed), rank(p.label, typed)) }))
    .filter((x) => x.r > 0)
    .sort((a, b) => b.r - a.r)
    .slice(0, CUSTOM_LIMIT)
    .map((x) => ({ ...item(x.p.key), id: `prop:${x.p.key}`, detail: x.p.label }));
  const rest = customFieldGroups(
    {
      ...data,
      customKeys: (data.customKeys ?? []).filter((k) => !defined.has(k)),
      campaignKeys: data.campaignKeys
        ? {
          ...data.campaignKeys,
          defined: data.campaignKeys.defined.filter((k) => !defined.has(k)),
          seen: data.campaignKeys.seen.filter((k) => !defined.has(k)),
        }
        : data.campaignKeys,
    },
    typed, v, item,
  );
  if (!propHits.length) return rest;
  return [{ key: 'properties', label: `${v.One('lead')} ${v.many('property')}`, items: propHits }, ...rest];
}

/** A property's values after the colon, by its type. */
function propertyValues(
  prop: SuggestProperty, keyText: string, typed: string, neg: string,
  term: (k: string, value: string) => string, today: string,
): SuggestionGroup[] {
  const label = prop.label || prop.key;
  const t = typed.toLowerCase();
  switch (prop.type) {
    case 'select':
    case 'multi_select': {
      const options = prop.options ?? [];
      const hits = typed ? best(options, (o) => o, typed, 8) : options.slice(0, 8);
      // A select is matched exactly; a multi-select holds several, so one
      // is matched anywhere in it.
      const exact = prop.type === 'select';
      return hits.length
        ? [{
          key: 'property-options',
          label,
          items: hits.map((o) => ({
            id: `opt:${o}`, label: o, detail: exact ? 'Is exactly this' : 'Includes this',
            insert: term(keyText, exact ? `=${o}` : o), done: true,
          })),
        }]
        : [];
    }
    case 'boolean': {
      const items: Suggestion[] = [
        { id: 'cv:true', label: 'true', detail: 'Yes', insert: term(keyText, 'true'), done: true },
        { id: 'cv:false', label: 'false', detail: 'No', insert: term(keyText, 'false'), done: true },
      ].filter((x) => !t || x.label.startsWith(t) || (x.detail ?? '').toLowerCase().startsWith(t));
      return items.length ? [{ key: 'property-values', label, items }] : [];
    }
    case 'number':
    case 'currency': {
      if (typed) return [];
      const unit = prop.type === 'currency' ? ' (pounds)' : '';
      const items: Suggestion[] = [
        { v: '>', d: 'More than' }, { v: '>=', d: 'At least' }, { v: '<', d: 'Less than' },
        { v: '<=', d: 'At most' }, { v: '=', d: 'Exactly' },
      ].map((e) => ({ id: `cv:${e.v}`, label: e.v, detail: `${e.d}${unit}`, insert: `${neg}${keyText}:${e.v}`, done: false }));
      return [{ key: 'property-values', label, items }];
    }
    case 'date':
    case 'datetime': {
      if (typed) return [];
      const month = today.slice(0, 7);
      const items: Suggestion[] = [
        { id: 'cv:today', label: `=${today}`, detail: 'On today', insert: term(keyText, `=${today}`), done: true },
        { id: 'cv:month', label: month, detail: 'In this month', insert: term(keyText, month), done: true },
        { id: 'cv:=', label: '=', detail: 'On a date: =YYYY-MM-DD', insert: `${neg}${keyText}:=`, done: false },
      ];
      return [{ key: 'property-values', label, items }];
    }
    default: {
      if (typed) return [];
      const items: Suggestion[] = [
        { id: 'cv:text', label: 'text', detail: `Contains it, anywhere in ${label}`, insert: `${neg}${keyText}:`, done: false },
        { id: 'cv:=', label: '=', detail: 'Exactly this text', insert: `${neg}${keyText}:=`, done: false },
      ];
      return [{ key: 'property-values', label, items }];
    }
  }
}

/**
 * Field names that are not defined properties: with a campaign in the
 * query its own first, else the account's as one group.
 */
function customFieldGroups(
  data: SuggestData, typed: string, v: Vocab, item: (k: string) => Suggestion,
): SuggestionGroup[] {
  const all = data.customKeys ?? [];
  const scoped = data.campaignKeys;
  if (!scoped || !scoped.campaigns.length) {
    const hits = best(all, (k) => k, typed, CUSTOM_LIMIT);
    return hits.length ? [{ key: 'custom-keys', label: 'Custom fields', items: hits.map(item) }] : [];
  }
  const defined = [...new Set(scoped.defined)];
  const seen = scoped.seen.filter((k) => !defined.includes(k));
  // Best match first, and otherwise in the order given: the campaign's own
  // order for its defined fields, most common first for the seen ones.
  const inOrder = (list: string[]) => list
    .map((k) => ({ k, r: rank(k, typed) }))
    .filter((x) => x.r > 0)
    .sort((a, b) => b.r - a.r)
    .map((x) => x.k);
  const mine = [...inOrder(defined), ...inOrder(seen)].slice(0, CUSTOM_LIMIT);
  const taken = new Set([...defined, ...seen]);
  const others = best(all.filter((k) => !taken.has(k)), (k) => k, typed, Math.max(CUSTOM_LIMIT - mine.length, 4));
  const only = scoped.campaigns.length === 1 ? scoped.campaigns[0] : undefined;
  const groups: SuggestionGroup[] = [];
  if (mine.length) {
    groups.push({
      key: 'campaign-keys',
      label: only ? `${only.name} fields` : `Fields on these ${v.many('campaign')}`,
      items: mine.map(item),
    });
  }
  if (others.length) groups.push({ key: 'custom-keys', label: 'Other fields', items: others.map(item) });
  return groups;
}

/** A bare word needs this many letters before it is offered as a filter value. */
const VALUE_MIN = 3;

/**
 * A bare word that is the start of a filter value, offered as that filter:
 * "delivered" suggests status:delivered, "yester" date:yesterday, "energy"
 * campaign:"Energy Premium". Only prefixes and word starts, so typing a
 * name to search for does not get buried under loose matches.
 */
function valueMatches(typed: string, neg: string, data: SuggestData, v: Vocab): Suggestion[] {
  if (typed.length < VALUE_MIN) return [];
  const strong = (name: string) => rank(name, typed) >= 60;
  const out: Suggestion[] = [];
  for (const s of Object.keys(STATUSES).filter(strong)) {
    out.push({ id: `v-status:${s}`, label: `status:${s}`, detail: statusLabel(s, v), insert: `${neg}status:${s}`, done: true });
  }
  for (const p of DATE_PRESETS.filter((p) => strong(p.word))) {
    out.push({ id: `v-date:${p.word}`, label: `date:${p.word}`, detail: dateDetail(p.word), insert: `${neg}date:${p.word}`, done: true });
  }
  const named = (list: Named[] | null, id: Exclude<KeyId, 'custom'>, what: string) => {
    for (const n of best((list ?? []).filter((n) => strong(n.name)), (n) => n.name, typed, 3)) {
      const k = keyName(id);
      out.push({ id: `v-${id}:${n.id}`, label: n.name, detail: what, insert: `${neg}${k}:${quote(n.name)}`, done: true, isName: true });
    }
  };
  named(data.campaigns, 'campaign', v.One('campaign'));
  named(data.suppliers, 'supplier', v.One('supplier'));
  named(data.buyers, 'buyer', v.One('buyer'));
  for (const s of best((data.sources ?? []).filter(strong), (s) => s, typed, 3)) {
    out.push({ id: `v-source:${s}`, label: `source:${s}`, detail: v.p.sourceTag, insert: `${neg}source:${quote(s)}`, done: true });
  }
  return out.slice(0, 8);
}

function dateDetail(word: string): string {
  switch (word) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'last7': return 'Last 7 days';
    case 'last30': return 'Last 30 days';
    case 'last90': return 'Last 90 days';
    case 'thisweek': return 'Since Monday';
    case 'thismonth': return 'This month';
    case 'lastmonth': return 'Last month';
    default: return '';
  }
}
